import axios from 'axios'
import { ROUTES } from '../constants/routes'

import {
  captureAuthSession,
  clearAuthSession,
  getAuthSession,
  isAuthSessionCurrent,
  replaceAuthSession,
  setExternalSessionChangeHandler,
} from './authSession'
import { getClientInstanceId } from './clientInstance'
import { normalizeApiError } from '../utils/apiError'
import { trackApiFailure, trackApiSuccess } from './posthogAnalytics'
import {
  AI_LEGAL_REQUIREMENT_EVENT,
  API_UNAVAILABLE_EVENT,
  CORE_LEGAL_REQUIREMENT_EVENT,
} from '../constants/events'

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 40_000,
  withCredentials: true,
  headers: {
    'X-CSRF-Protection': '1',
  },
})

// 동시에 여러 요청이 401을 받아도 토큰 재발급 요청은 하나만 실행한다.
let tokenRequest = null
const AUTH_SESSION_LOCK_NAME = 'akive-auth-session'
const AUTH_LIFECYCLE_PATHS = new Set([
  '/auth/login/email',
  '/auth/tokens',
  '/auth/tokens/refresh',
  '/auth/tokens/refresh/current',
])

function getRequestPath(config = {}) {
  try {
    return new URL(config.url, api.defaults?.baseURL || window.location.origin).pathname
  } catch {
    return config.url?.split('?')[0] || ''
  }
}

function isAuthLifecycleRequest(config) {
  return AUTH_LIFECYCLE_PATHS.has(getRequestPath(config))
}

function runWithAuthSessionLock(callback) {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(AUTH_SESSION_LOCK_NAME, callback)
  }
  return callback()
}

function notifyApiUnavailable(error) {
  const hasNoResponse = !error?.response
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
  const isCancelled = error?.code === 'ERR_CANCELED'

  if (!hasNoResponse || isOffline || isCancelled || typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(API_UNAVAILABLE_EVENT))
}

function notifyLegalRequirement(error, config) {
  if (error?.response?.status !== 403 || typeof window === 'undefined') return
  const code = error.response.data?.code
  const requestPath = getRequestPath(config)
  if (requestPath.startsWith('/legal-documents/')) return

  if (code === 'CORE_SERVICE_LEGAL_REQUIREMENTS_NOT_SATISFIED') {
    if (requestPath.startsWith('/members/me/withdraw')) return
    window.dispatchEvent(new CustomEvent(CORE_LEGAL_REQUIREMENT_EVENT))
  }
  if (code === 'AI_FEATURE_LEGAL_REQUIREMENTS_NOT_SATISFIED') {
    window.dispatchEvent(new CustomEvent(AI_LEGAL_REQUIREMENT_EVENT))
  }
}

function requestToken({ skipAuthRedirect = false, notifySessionChange = false } = {}) {
  if (!tokenRequest) {
    const capturedSession = captureAuthSession()

    tokenRequest = runWithAuthSessionLock(() => api.post('/auth/tokens', {}, {
      _skipAuthRefresh: true,
      _skipAuthRedirect: skipAuthRedirect,
    }))
      .then((result) => {
        const newToken = result?.data

        if (!isAuthSessionCurrent(capturedSession)) {
          const sessionChangedError = new Error('토큰 갱신 중 인증 세션이 변경됐어요.')
          sessionChangedError.code = 'ERR_CANCELED'
          throw sessionChangedError
        }

        replaceAuthSession({
          ...capturedSession.session,
          ...newToken,
          clientInstanceId: capturedSession.session?.clientInstanceId || getClientInstanceId(),
        }, { announce: false, notify: notifySessionChange })
        return newToken
      })
      .finally(() => {
        tokenRequest = null
      })
  }

  return tokenRequest
}

/**
 * API 요청 시마다 액세스 토큰을 실어 보내도록 한다.
 */
api.interceptors.request.use(
  function (config) {
    const user = getAuthSession()
    if (user?.accessToken && !isAuthLifecycleRequest(config)) {
      config.headers.Authorization = `Bearer ${user.accessToken}`
    }
    return config
  }
)

/**
 * 정상 응답일 경우 응답을 그대로 리턴한다.
 * 단, 액세스 토큰이 만료 됐을 경우 리프레시 토큰을 통해 토큰을 재발행 하도록 한다.
 */
api.interceptors.response.use(
  function (response) {
    trackApiSuccess(response)
    return response
  },
  async function (error) {
    const originalReq = error.config || {}

    if (typeof error.response?.data === 'string') {
      try {
        error.response.data = JSON.parse(error.response.data)
      } catch {
        // HTML이나 일반 문자열 응답은 상태 코드 기준의 사용자 문구로 처리한다.
      }
    }

    if (error.response && error.response.status === 401
      && error.response.data?.code === 'ACCESS_TOKEN_EXPIRED'
      && !originalReq._skipAuthRefresh
      && !isAuthLifecycleRequest(originalReq)
      && !originalReq._retry) {

      originalReq._retry = true

      try {
        // 액세스 토큰이 만료됐을 경우 리프레시 토큰을 통해 재발행
        const newToken = await requestToken()

        // 새로 발급 받은 토큰으로 원래 하려던 API 요청 시도
        originalReq.headers.Authorization = `Bearer ${newToken.accessToken}`
        return api.request(originalReq)
      } catch (refreshError) {
        if (refreshError?.code === 'ERR_CANCELED') {
          return Promise.reject(normalizeApiError(error))
        }
        const normalizedError = normalizeApiError(refreshError)
        trackApiFailure(normalizedError, originalReq)
        return Promise.reject(normalizedError)
      }
    } else if (error.response?.status === 401
      && ['INVALID_REFRESH_TOKEN', 'REFRESH_TOKEN_EXPIRED'].includes(error.response.data?.code)) {
      clearAuthSession()
      if (!originalReq._skipAuthRedirect) window.location.href = ROUTES.LOGIN
      return Promise.reject(normalizeApiError(error))
    } else {
      const normalizedError = normalizeApiError(error)
      trackApiFailure(normalizedError, originalReq)
      notifyLegalRequirement(normalizedError, originalReq)
      notifyApiUnavailable(normalizedError)
      return Promise.reject(normalizedError)
    }
  }
)

async function restoreAuthSession() {
  try {
    return await requestToken({ skipAuthRedirect: true, notifySessionChange: true })
  } catch (error) {
    // Refresh Token이 없거나 만료된 401은 비로그인 상태로 처리한다.
    // 네트워크 오류와 5xx는 인터셉터가 별도의 서비스 장애 상태로 알린다.
    return null
  }
}

setExternalSessionChangeHandler(() => {
  restoreAuthSession().catch(() => {})
})

export { restoreAuthSession, runWithAuthSessionLock }
export default api
